---
title: "Multi-Timeframe Analysis"
slug: multi-timeframe-analysis
shortTitle: "Multi-Timeframe Analysis"
description: "How to read a market top-down across three timeframes: the higher one for trend, the middle for setups and the lower for entries, with a worked example."
level: intermediate
category: trading
course: intermediate
order: 8
topics: [technical-analysis, trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/multi-timeframe-analysis.svg
faqs:
  - q: "How many timeframes should I use?"
    a: "Three is the usual number: one for the trend, one for the setup and one for the entry. Two can work for simpler approaches. More than three tends to produce conflicting signals and slows decisions down."
  - q: "How far apart should the timeframes be?"
    a: "A common guideline is a factor of about four to six between them, for example 1D, 4h and 1h, or 1h, 15m and 5m. Timeframes too close together show nearly the same picture; too far apart and the lower one loses its connection to the higher one."
  - q: "What should I do when the timeframes disagree?"
    a: "Many traders simply wait for them to line up. Others trade smaller, or treat the trade as a short counter-trend move with a closer target. What matters is deciding your rule in advance rather than in the moment."
  - q: "Is there a weekly chart on the platform?"
    a: "The trade chart's timeframes run from 1 minute to 1 day. For a weekly view you can use a separate charting tool, and the Markets page quick view gives longer-term context such as the daily trend and 30-day and 52-week highs and lows."
  - q: "Which timeframe should my stop loss come from?"
    a: "Usually from the timeframe that defines your trade idea, often the middle one. A stop based only on a 1-minute chart is often so tight that normal noise reaches it."
---

Open a 15-minute chart and you might see a clear downtrend. Switch to the daily chart and the same move may be nothing more than a small dip in a strong uptrend. Neither view is wrong; they answer different questions. **Multi-timeframe analysis** puts them together, so you trade in the direction of the bigger picture and use the smaller one only to time your entry.

This lesson sets out a simple three-timeframe framework, a step-by-step top-down routine, a worked Bitcoin example and the mistakes that trip people up, using the timeframes available on the {{BRAND}} chart.

<CTA />

<KeyTakeaways>

- **Higher sets direction.** The higher timeframe shows the trend and the major levels, and decides which side you look for.
- **Middle finds the setup.** The middle timeframe shows pullbacks and patterns at those levels.
- **Lower times the entry.** The lower timeframe gives a precise trigger and a tighter, but still logical, stop.
- **Agreement matters.** When the timeframes disagree, waiting or trading smaller is a legitimate decision.

</KeyTakeaways>

## Why one timeframe is not enough

Every timeframe is a slice of the same price history. A daily candle contains 24 hourly candles; an hourly candle contains 60 one-minute candles. Trends, levels and patterns exist on all of them at once, often pointing in different directions.

A trader who only watches a 5-minute chart can be buying confidently right into a daily resistance level they never saw. A trader who only watches the daily chart may have the direction right but enter at a poor price with a very wide stop. Looking at both fixes both problems.

## The three-timeframe framework

The usual approach uses three timeframes, each roughly four to six times the next:

1. **Higher timeframe: the context.** Which way is the trend, and where are the major support and resistance levels?
2. **Middle timeframe: the setup.** Is price pulling back to a level, forming a pattern or breaking out?
3. **Lower timeframe: the trigger.** Is there a specific signal, such as a break of a small swing or a strong candle, to enter on?

<CompareTable caption="Example timeframe sets by trading style">

| Style | Higher (context) | Middle (setup) | Lower (entry) |
| --- | --- | --- | --- |
| Scalping | 1h | 15m | 1m |
| Day trading | 4h | 1h | 15m |
| Swing trading | 1D | 4h | 1h |
| Longer-term swing | Weekly (separate tool) | 1D | 4h |

</CompareTable>

The exact set matters less than using the same one consistently. [Scalping vs Day Trading vs Swing Trading](/academy/article/scalping-day-swing-trading) explains how to choose a style in the first place.

## A top-down routine

<Steps>
<Step title="Read the higher timeframe">

Identify the trend from the sequence of swing highs and lows, as in [Trendlines, Channels and Market Structure](/academy/article/trendlines-channels-market-structure), and mark the major levels. Decide which direction you will look for.

</Step>
<Step title="Wait for a setup on the middle timeframe">

Look for price to reach a level that matters on the higher timeframe, such as a pullback to former resistance turned support in an uptrend.

</Step>
<Step title="Find the trigger on the lower timeframe">

Wait for the lower chart to show price turning in your direction, for example a break above the last small swing high or a strong engulfing candle.

</Step>
<Step title="Set the stop from the setup">

Place the stop where the middle-timeframe idea is proven wrong, usually beyond the pullback's swing low or high, then size the position from that distance.

</Step>
<Step title="Take targets from the higher timeframe">

The next major level on the higher timeframe is a natural target, because that is where the bigger picture may push back.

</Step>
</Steps>

## A worked example

<Example title="Top-down on BTC/USDT (illustrative prices)">

**1D:** Bitcoin is making higher highs and higher lows. The last daily high is 61,500, and an old resistance area near 59,000 was broken two weeks ago.

**4h:** Price pulls back to 59,000 and stalls there, forming a swing low at 58,800.

**1h:** A strong up candle breaks the last small hourly swing high. You enter at **59,400**.

- Stop: below the 4-hour swing low at **58,700**, a distance of 59,400 − 58,700 = 700
- Target: the daily high at **61,500**, a distance of 61,500 − 59,400 = 2,100
- Reward-to-risk: 2,100 ÷ 700 = **3 to 1**
- Risk budget: 70 USDT, so size = 70 ÷ 700 = **0.1 BTC**, which is 0.10 lots if 1 lot is 1 BTC
- Value of a 1 USDT move: 0.1 USDT; at the stop the loss is about 70 USDT before costs

</Example>

{/* TODO: verify with platform — BTC contract size (1 lot = 1 BTC assumed). */}

Notice what each timeframe did. The daily chart chose the direction and the target. The 4-hour chart chose the area and the stop. The hourly chart only chose the moment. That combination of a nearby stop and a distant target is why top-down trades can offer favorable reward-to-risk ratios, although a farther target is also reached less often. [Risk/Reward Ratio and Win Rate](/academy/article/risk-reward-and-win-rate) explains that trade-off.

## When timeframes disagree

Disagreement is normal. The daily chart may be rising while the 4-hour chart is in a clear decline. You have three reasonable options:

- **Wait** until the lower timeframes turn back in the direction of the higher one.
- **Trade smaller**, accepting that the odds are less clear.
- **Treat it as a counter-trend trade**, with a closer target and a quicker exit.

Decide which you will use before you open the chart. Choosing in the moment usually means choosing whatever fits the trade you already want.

Also watch for the point where a lower-timeframe move grows large enough to change the higher picture. If that 4-hour decline carried price to a close below the last daily swing low, the daily uptrend itself would be in question, and the right response would be to reassess the context rather than keep looking for buy setups.

## Common mistakes

- **Timeframe shopping.** Flicking through charts until one agrees with what you want to do.
- **Too many timeframes.** Five or six views almost always contain a conflict, which leads to hesitation.
- **Stops from the lowest timeframe.** A 1-minute stop can be so tight that ordinary noise and the spread hit it.
- **Forgetting the candle is still open.** A higher-timeframe candle that is only halfway through can still change shape completely.

<Warning>

Lower timeframes are noisy, and the spread is a larger share of small moves. Combined with leverage, very tight stops on 1-minute or 5-minute charts can lead to a fast series of small losses. Make sure your stop distance reflects the timeframe your idea comes from, and size the position so the total loss at the stop is one you accept.

</Warning>

## Multi-timeframe analysis on {{BRAND}}

The trade chart offers **1m, 5m, 15m, 1h, 4h and 1D**, so you can switch between your three timeframes with one click. For a weekly view, use a separate charting tool. The **Markets** page quick view adds longer-range context, including its **Daily trend** section and 7-day, 30-day and 52-week highs and lows.

<Screenshot name="trade-chart-timeframes" alt="The trade chart's timeframe buttons from 1m to 1D" caption="Switch between your higher, middle and lower timeframes with the timeframe buttons." />

{/* TODO: verify with platform — quick view stats shown for each asset class. */}

<ProTip>

Set price alerts at the higher-timeframe levels you care about, using the bell on a market row or **Account → Alerts**. Then you only need to open the lower timeframes when price is actually in your zone, rather than staring at a 1-minute chart all day. Turning on the **Candle-close countdown** in the chart settings also reminds you when a candle is still forming.

</ProTip>

## Conclusion

Multi-timeframe analysis is a way of asking three questions in order: which way is the market going, where is a sensible place to join it, and when exactly should you act? The higher timeframe answers the first, the middle the second, the lower the third. Keep the set consistent, take your stop from the timeframe that defines the idea, size from that distance, and when the timeframes disagree, remember that waiting is a valid decision.
